// First Created - 14 Oct 2026
// Author - Atul Pandey
// Purpose - EtAdmin list page for Trinity Meeting Board
//
// Trinity Meeting Board:
// - Creates native Google Calendar meetings for Trinity participants.
// - Trinity performs only the initial meeting / recurring series creation.
// - Google Calendar owns the meeting lifecycle after creation.
// - This page shows Trinity's record of the initial creation.
//
// Important:
// - Existing SlotBoard Google Calendar flow remains untouched.
// - participantUserIds is the resolved participant snapshot.
// - Google Calendar status reflects initial creation only.
//
// Updated - 16 Oct 2026 - added meeting / participant search
// Updated - 16 Oct 2026 - display participant name / email in details

import React, {
    useMemo,
    useState,
} from "react"

import useTitle from "@/hooks/useTitle"

import {
    addIcon,
} from "@/assets/img/imgAssets"

import {
    PlusIcon,
    MinusIcon,
    BuildingOfficeIcon,
    UserGroupIcon,
    CalendarDaysIcon,
    ClockIcon,
    ArrowPathIcon,
    VideoCameraIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    DocumentTextIcon,
    MagnifyingGlassIcon,
    EnvelopeIcon,
} from "@heroicons/react/24/outline"

import PulseLoader from "react-spinners/PulseLoader"

import {
    useGetTrinityMeetingsQuery,
} from "@/features/trinityMeetings/trinityMeetingsApiSlice"


/*
|--------------------------------------------------------------------------
| 01 - Resolve Populated Or Plain ID
|--------------------------------------------------------------------------
*/

const getRefId = (
    value
) => {

    if (
        !value
    ) {

        return "not-defined"

    }


    if (
        typeof value ===
        "string"
    ) {

        return value

    }


    return (
        value?._id
        ||
        value?.id
        ||
        "not-defined"
    )

}


/*
|--------------------------------------------------------------------------
| 02 - Participant Name
|--------------------------------------------------------------------------
*/

const getParticipantName = (
    participant
) => {

    if (
        !participant
        ||
        typeof participant ===
        "string"
    ) {

        return ""

    }


    return (
        participant?.name
        ||
        participant?.username
        ||
        ""
    )

}


/*
|--------------------------------------------------------------------------
| 03 - Participant Email
|--------------------------------------------------------------------------
*/

const getParticipantEmail = (
    participant
) => {

    if (
        !participant
        ||
        typeof participant ===
        "string"
    ) {

        return ""

    }


    return (
        participant?.email
        ||
        ""
    )

}


/*
|--------------------------------------------------------------------------
| 04 - Date Formatting
|--------------------------------------------------------------------------
*/

const formatMeetingDate = (
    value,
    timezone
) => {

    if (
        !value
    ) {

        return "Not defined"

    }


    const date =
        new Date(
            value
        )


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return "Not defined"

    }


    try {

        return new Intl.DateTimeFormat(
            "en-US",
            {

                timeZone:
                    timezone
                    ||
                    "America/Los_Angeles",

                month:
                    "short",

                day:
                    "numeric",

                year:
                    "numeric",

            }
        ).format(
            date
        )

    } catch (
        error
    ) {

        return date.toLocaleDateString(
            "en-US",
            {

                month:
                    "short",

                day:
                    "numeric",

                year:
                    "numeric",

            }
        )

    }

}


/*
|--------------------------------------------------------------------------
| 05 - Time Formatting
|--------------------------------------------------------------------------
*/

const formatMeetingTime = (
    value,
    timezone
) => {

    if (
        !value
    ) {

        return "Not defined"

    }


    const date =
        new Date(
            value
        )


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return "Not defined"

    }


    try {

        return new Intl.DateTimeFormat(
            "en-US",
            {

                timeZone:
                    timezone
                    ||
                    "America/Los_Angeles",

                hour:
                    "numeric",

                minute:
                    "2-digit",

                timeZoneName:
                    "short",

            }
        ).format(
            date
        )

    } catch (
        error
    ) {

        return date.toLocaleTimeString(
            "en-US",
            {

                hour:
                    "numeric",

                minute:
                    "2-digit",

            }
        )

    }

}


/*
|--------------------------------------------------------------------------
| 06 - Recurrence Day Labels
|--------------------------------------------------------------------------
*/

const recurrenceDayLabels = {

    MO:
        "Mon",

    TU:
        "Tue",

    WE:
        "Wed",

    TH:
        "Thu",

    FR:
        "Fri",

    SA:
        "Sat",

    SU:
        "Sun",

}


/*
|--------------------------------------------------------------------------
| 07 - Recurrence Label
|--------------------------------------------------------------------------
*/

const getRecurrenceLabel = (
    meeting
) => {

    if (
        meeting
            ?.recurrenceType !==
        "recurring"
    ) {

        return "One Time"

    }


    const interval =
        Number(
            meeting
                ?.recurrenceInterval
            ||
            1
        )


    const days =
        (
            meeting
                ?.recurrenceDaysOfWeek
            ||
            []
        )
            .map(
                day =>
                    recurrenceDayLabels[
                        day
                    ]
                    ||
                    day
            )
            .join(
                ", "
            )


    let label


    if (
        meeting
            ?.recurrenceFrequency ===
        "daily"
    ) {

        label =
            interval ===
            1
                ?
                "Daily"
                :
                `Every ${interval} Days`

    }


    else if (
        meeting
            ?.recurrenceFrequency ===
        "monthly"
    ) {

        if (
            interval ===
            1
        ) {

            label =
                "Monthly"

        }


        else if (
            interval ===
            3
        ) {

            label =
                "Quarterly"

        }


        else if (
            interval ===
            6
        ) {

            label =
                "Semi Annually"

        }


        else if (
            interval ===
            12
        ) {

            label =
                "Annually"

        }


        else {

            label =
                `Every ${interval} Months`

        }

    }


    else {

        label =
            interval ===
            1
                ?
                "Weekly"
                :
                interval ===
                2
                    ?
                    "Biweekly"
                    :
                    `Every ${interval} Weeks`

    }


    if (
        days
        &&
        [
            "weekly",
            "monthly",
        ].includes(
            meeting
                ?.recurrenceFrequency
        )
    ) {

        label +=
            ` · ${days}`

    }


    if (
        meeting
            ?.recurrenceEndType ===
        "after_occurrences"
        &&
        meeting
            ?.recurrenceOccurrenceCount
    ) {

        label +=
            ` · ${meeting.recurrenceOccurrenceCount} Occurrences`

    }


    else if (
        meeting
            ?.recurrenceEndType ===
        "on_date"
        &&
        meeting
            ?.recurrenceEndDate
    ) {

        label +=
            ` · Through ${formatMeetingDate(
                meeting.recurrenceEndDate,
                meeting.meetingTimezone
            )}`

    }


    return label

}


/*
|--------------------------------------------------------------------------
| 08 - Meeting Board Status
|--------------------------------------------------------------------------
*/

const getMeetingBoardStatus = (
    meeting
) => {

    if (
        meeting
            ?.calendarCreationStatus ===
        "failed"
    ) {

        return {

            key:
                "needs_attention",

            label:
                "Needs Attention",

            className:
                "bg-red-100 text-red-700",

        }

    }


    if (
        meeting
            ?.calendarCreationStatus ===
        "pending"
    ) {

        return {

            key:
                "pending",

            label:
                "Calendar Pending",

            className:
                "bg-yellow-100 text-yellow-700",

        }

    }


    if (
        meeting
            ?.calendarCreationStatus ===
        "created"
        &&
        meeting
            ?.meetingStatus ===
        "scheduled"
    ) {

        return {

            key:
                "scheduled",

            label:
                "Calendar Created",

            className:
                "bg-lime-100 text-gray-700",

        }

    }


    return {

        key:
            "draft",

        label:
            "Draft",

        className:
            "bg-gray-200 text-gray-700",

    }

}


/*
|--------------------------------------------------------------------------
| 09 - Participant Count
|--------------------------------------------------------------------------
*/

const getParticipantCount = (
    meeting
) => {

    if (
        Number.isFinite(
            meeting
                ?.participantCount
        )
    ) {

        return meeting
            .participantCount

    }


    return (
        meeting
            ?.participantUserIds
            ?.length
        ||
        0
    )

}


/*
|--------------------------------------------------------------------------
| 10 - Searchable Meeting Text
|--------------------------------------------------------------------------
|
| Search covers:
|
| - meeting name
| - resolved Trinity participant name
| - resolved Trinity participant email
| - additional external invite email
|
*/

const getMeetingSearchText = (
    meeting
) => {

    const participants =
        Array.isArray(
            meeting
                ?.participantUserIds
        )
            ?
            meeting
                .participantUserIds
            :
            []


    const participantSearchValues =
        participants
            .flatMap(
                participant => [

                    getParticipantName(
                        participant
                    ),

                    getParticipantEmail(
                        participant
                    ),

                ]
            )


    const additionalInviteEmails =
        Array.isArray(
            meeting
                ?.additionalInviteEmails
        )
            ?
            meeting
                .additionalInviteEmails
            :
            []


    return [

        meeting
            ?.meetingName,

        ...participantSearchValues,

        ...additionalInviteEmails,

    ]
        .filter(
            Boolean
        )
        .join(
            " "
        )
        .toLowerCase()

}

/*
|--------------------------------------------------------------------------
| 10b - Highlight Search Text
|--------------------------------------------------------------------------
|
| Highlights the matching portion of visible result text without changing the
| underlying value.
|
| Search remains case insensitive.
|
*/

const highlightSearchText = (
    value,
    searchText
) => {

    if (
        value ===
        undefined
        ||
        value ===
        null
    ) {

        return value

    }


    const text =
        String(
            value
        )


    const normalizedSearch =
        String(
            searchText
            ||
            ""
        )
            .trim()


    if (
        !normalizedSearch
    ) {

        return text

    }


    /*
    |--------------------------------------------------------------------------
    | Escape Regular Expression Characters
    |--------------------------------------------------------------------------
    |
    | Allows searches such as:
    |
    | john.smith@company.com
    | test+meeting@company.com
    |
    | without those characters being interpreted as regex operators.
    |
    */

    const escapedSearch =
        normalizedSearch
            .replace(
                /[.*+?^${}()|[\]\\]/g,
                "\\$&"
            )


    const searchPattern =
        new RegExp(
            `(${escapedSearch})`,
            "gi"
        )


    const normalizedSearchLower =
        normalizedSearch
            .toLowerCase()


    return text
        .split(
            searchPattern
        )
        .map(
            (
                part,
                index
            ) => {

                if (
                    part
                        .toLowerCase() ===
                    normalizedSearchLower
                ) {

                    return (

                        <mark
                            key={
                                `${part}-${index}`
                            }

                            className="bg-yellow-200 text-inherit rounded-sm px-[1px]"
                        >
                            {
                                part
                            }
                        </mark>

                    )

                }


                return part

            }
        )

}


/*
|--------------------------------------------------------------------------
| 10c - Participant / Email Search Matches
|--------------------------------------------------------------------------
|
| When a meeting is found because of a participant name or email, surface the
| matching value directly on the collapsed result card.
|
| This makes it clear why the meeting matched without requiring the user to
| expand the meeting first.
|
*/

const getParticipantSearchMatches = (
    meeting,
    searchText
) => {

    const normalizedSearch =
        String(
            searchText
            ||
            ""
        )
            .trim()
            .toLowerCase()


    if (
        !normalizedSearch
    ) {

        return []

    }


    const participants =
        Array.isArray(
            meeting
                ?.participantUserIds
        )
            ?
            meeting
                .participantUserIds
            :
            []


    const additionalInviteEmails =
        Array.isArray(
            meeting
                ?.additionalInviteEmails
        )
            ?
            meeting
                .additionalInviteEmails
            :
            []


    const searchableParticipantValues =
        participants
            .flatMap(
                participant => [

                    getParticipantName(
                        participant
                    ),

                    getParticipantEmail(
                        participant
                    ),

                ]
            )


    return [

        ...new Set([

            ...searchableParticipantValues,

            ...additionalInviteEmails,

        ]
            .filter(
                Boolean
            )
            .filter(
                value =>
                    String(
                        value
                    )
                        .toLowerCase()
                        .includes(
                            normalizedSearch
                        )
            )
        ),

    ]

}


/*
|--------------------------------------------------------------------------
| 11 - Component
|--------------------------------------------------------------------------
*/

const EtTrinityMeetingList = ({
    onAdd,
}) => {

    // First Created - 14 Oct 2026
    // Author - Atul Pandey
    // Purpose - EtAdmin Meeting Board list page


    useTitle(
        "Trinity Meeting Board"
    )


    /*
    |--------------------------------------------------------------------------
    | 12 - UI State
    |--------------------------------------------------------------------------
    */

    const [
        selectedOrgId,
        setSelectedOrgId,
    ] =
        useState(
            "all"
        )


    const [
        selectedStatus,
        setSelectedStatus,
    ] =
        useState(
            "all"
        )


    const [
        meetingSearch,
        setMeetingSearch,
    ] =
        useState(
            ""
        )


    const [
        showDetails,
        setShowDetails,
    ] =
        useState({})


    /*
    |--------------------------------------------------------------------------
    | 13 - Fetch Meetings
    |--------------------------------------------------------------------------
    */

    const {

        data:
            trinityMeetingsData,

        isError,

        error,

        isLoading,

        isFetching,

    } =
        useGetTrinityMeetingsQuery()


    /*
    |--------------------------------------------------------------------------
    | 14 - Normalize Meetings
    |--------------------------------------------------------------------------
    */

    const meetings =
        useMemo(
            () => {

                return (
                    trinityMeetingsData
                        ?.ids
                        ?.map(
                            id => {

                                const meeting =
                                    trinityMeetingsData
                                        ?.entities[
                                        id
                                    ]


                                return {

                                    ...meeting,

                                    _id:
                                        meeting
                                            ?._id,

                                }

                            }
                        )
                    ||
                    []
                )

            },
            [
                trinityMeetingsData,
            ]
        )

    // console.log(
    //     "TRINITY MEETINGS NORMALIZED:",
    //     meetings
    // )

    // console.log(
    //     "FIRST MEETING PARTICIPANTS:",
    //     meetings?.[0]?.participantUserIds
    // )

    /*
    |--------------------------------------------------------------------------
    | 15 - Organizations
    |--------------------------------------------------------------------------
    */

    const organizations =
        useMemo(
            () => {

                const orgMap =
                    new Map()


                meetings
                    .forEach(
                        meeting => {

                            const orgId =
                                getRefId(
                                    meeting
                                        ?.orgId
                                )


                            if (
                                orgId ===
                                "not-defined"
                            ) {

                                return

                            }


                            if (
                                !orgMap.has(
                                    orgId
                                )
                            ) {

                                orgMap.set(
                                    orgId,
                                    {

                                        _id:
                                            orgId,

                                        organizationName:
                                            meeting
                                                ?.orgId
                                                ?.organizationName
                                            ||
                                            "Organization",

                                    }
                                )

                            }

                        }
                    )


                return Array
                    .from(
                        orgMap.values()
                    )
                    .sort(
                        (
                            a,
                            b
                        ) =>
                            (
                                a
                                    ?.organizationName
                                ||
                                ""
                            )
                                .localeCompare(
                                    b
                                        ?.organizationName
                                    ||
                                    ""
                                )
                    )

            },
            [
                meetings,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 16 - Organization Filter
    |--------------------------------------------------------------------------
    */

    const organizationMeetings =
        useMemo(
            () => {

                if (
                    selectedOrgId ===
                    "all"
                ) {

                    return meetings

                }


                return meetings
                    .filter(
                        meeting =>
                            getRefId(
                                meeting
                                    ?.orgId
                            ) ===
                            selectedOrgId
                    )

            },
            [
                meetings,
                selectedOrgId,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 17 - Status Counts
    |--------------------------------------------------------------------------
    */

    const statusCounts =
        useMemo(
            () => {

                const counts =
                    {

                        all:
                            organizationMeetings
                                .length,

                        scheduled:
                            0,

                        needs_attention:
                            0,

                        pending:
                            0,

                        draft:
                            0,

                    }


                organizationMeetings
                    .forEach(
                        meeting => {

                            const status =
                                getMeetingBoardStatus(
                                    meeting
                                )


                            if (
                                counts[
                                    status.key
                                ] !==
                                undefined
                            ) {

                                counts[
                                    status.key
                                ] +=
                                    1

                            }

                        }
                    )


                return counts

            },
            [
                organizationMeetings,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 18 - Status + Search Filter
    |--------------------------------------------------------------------------
    */

    const filteredMeetings =
        useMemo(
            () => {

                let filtered =
                    organizationMeetings


                if (
                    selectedStatus !==
                    "all"
                ) {

                    filtered =
                        filtered
                            .filter(
                                meeting =>
                                    getMeetingBoardStatus(
                                        meeting
                                    ).key ===
                                    selectedStatus
                            )

                }


                const normalizedSearch =
                    meetingSearch
                        .trim()
                        .toLowerCase()


                if (
                    normalizedSearch
                ) {

                    filtered =
                        filtered
                            .filter(
                                meeting =>
                                    getMeetingSearchText(
                                        meeting
                                    )
                                        .includes(
                                            normalizedSearch
                                        )
                            )

                }


                return filtered
                    .slice()
                    .sort(
                        (
                            a,
                            b
                        ) => {

                            const aStart =
                                a
                                    ?.meetingStart
                                    ?
                                    new Date(
                                        a.meetingStart
                                    ).getTime()
                                    :
                                    0


                            const bStart =
                                b
                                    ?.meetingStart
                                    ?
                                    new Date(
                                        b.meetingStart
                                    ).getTime()
                                    :
                                    0


                            return (
                                bStart -
                                aStart
                            )

                        }
                    )

            },
            [
                organizationMeetings,
                selectedStatus,
                meetingSearch,
            ]
        )


    /*
    |--------------------------------------------------------------------------
    | 19 - Toggle Details
    |--------------------------------------------------------------------------
    */

    const handleToggleDetails = (
        meetingId
    ) => {

        setShowDetails(
            prev => ({

                ...prev,

                [meetingId]:
                    !prev[
                        meetingId
                    ],

            })
        )

    }


    /*
    |--------------------------------------------------------------------------
    | 20 - Loading
    |--------------------------------------------------------------------------
    */

    if (
        isLoading
    ) {

        return (

            <PulseLoader
                color={
                    "#ea580c"
                }
            />

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 21 - Error
    |--------------------------------------------------------------------------
    */

    if (
        isError
    ) {

        return (

            <div
                className="text-sm text-orange-700"
            >

                Error occurred:{" "}

                {
                    error
                        ?.data
                        ?.message
                    ||
                    "Unable to load Trinity meetings."
                }

            </div>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 22 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto"
        >


            {/* Header */}

            <div
                className="flex justify-between items-center m-1 p-1"
            >

                <div
                    className="flex justify-start items-center"
                >

                    <p
                        className="text-2xl m-1 p-1 font-bold"
                    >
                        Trinity Meeting Board
                    </p>


                    <img
                        src={
                            addIcon
                        }

                        alt=""

                        className="h-10 w-10 cursor-pointer"

                        onClick={
                            onAdd
                        }
                    />

                </div>


                <div
                    className="flex justify-start items-center mx-2"
                >

                    {
                        isFetching
                        &&
                        (

                            <p
                                className="text-[9px] text-gray-400 mx-2"
                            >
                                Refreshing...
                            </p>

                        )
                    }


                    <p
                        className="text-[10px] text-gray-500 mx-1"
                    >
                        Total:
                    </p>


                    <div
                        className="rounded-full flex justify-center items-center h-6 w-6 border border-orange-600 bg-white"
                    >

                        <p
                            className="text-orange-600 text-[10px] font-bold"
                        >
                            {
                                meetings
                                    .length
                            }
                        </p>

                    </div>

                </div>

            </div>


            {/* Context */}

            <div
                className="bg-white rounded m-1 p-3"
            >

                <p
                    className="text-xs text-gray-600"
                >
                    Create native Google Calendar meetings for Trinity
                    participants. Trinity handles the initial meeting creation;
                    subsequent meeting management continues directly in Google
                    Calendar.
                </p>

            </div>


            {/* Organization / Status / Search */}

            <div
                className="bg-white rounded m-1 p-3"
            >

                <div
                    className="flex justify-start items-center"
                >

                    <BuildingOfficeIcon
                        className="h-5 w-5 text-orange-600 mx-2"
                    />


                    <p
                        className="text-xs font-bold mr-3"
                    >
                        Organization
                    </p>


                    <select
                        value={
                            selectedOrgId
                        }

                        onChange={
                            event =>
                                setSelectedOrgId(
                                    event
                                        .target
                                        .value
                                )
                        }

                        className="border border-gray-300 rounded bg-white text-xs px-3 py-2 min-w-[260px]"
                    >

                        <option
                            value="all"
                        >
                            All Organizations
                        </option>


                        {
                            organizations
                                .map(
                                    organization => (

                                        <option
                                            key={
                                                organization
                                                    ._id
                                            }

                                            value={
                                                organization
                                                    ._id
                                            }
                                        >
                                            {
                                                organization
                                                    .organizationName
                                            }
                                        </option>

                                    )
                                )
                        }

                    </select>

                </div>


                {/* Status Chips */}

                <div
                    className="flex justify-start items-center flex-wrap mt-3"
                >

                    {
                        [
                            {
                                key:
                                    "all",

                                label:
                                    "All",
                            },

                            {
                                key:
                                    "scheduled",

                                label:
                                    "Scheduled",
                            },

                            {
                                key:
                                    "needs_attention",

                                label:
                                    "Needs Attention",
                            },

                            {
                                key:
                                    "pending",

                                label:
                                    "Pending",
                            },

                            {
                                key:
                                    "draft",

                                label:
                                    "Draft",
                            },
                        ]
                            .map(
                                status => (

                                    <button
                                        key={
                                            status.key
                                        }

                                        type="button"

                                        onClick={
                                            () =>
                                                setSelectedStatus(
                                                    status.key
                                                )
                                        }

                                        className={`text-[10px] rounded-full mx-1 px-3 py-1 border ${
                                            selectedStatus ===
                                            status.key
                                                ?
                                                "bg-orange-600 text-white border-orange-600"
                                                :
                                                "bg-white text-gray-600 border-gray-300"
                                        }`}
                                    >
                                        {
                                            status.label
                                        }

                                        {" "}

                                        {
                                            statusCounts[
                                                status.key
                                            ]
                                        }
                                    </button>

                                )
                            )
                    }

                </div>


                {/* Search */}

                <div
                    className="flex justify-start items-center mt-3"
                >

                    <div
                        className="relative flex-1 max-w-2xl"
                    >

                        <MagnifyingGlassIcon
                            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                        />


                        <input
                            type="text"

                            value={
                                meetingSearch
                            }

                            onChange={
                                event =>
                                    setMeetingSearch(
                                        event
                                            .target
                                            .value
                                    )
                            }

                            placeholder="Search by meeting name, participant name or email"

                            className="w-full border border-gray-300 rounded bg-white text-xs pl-9 pr-3 py-2"
                        />

                    </div>


                    {
                        meetingSearch
                            .trim()
                        &&
                        (

                            <button
                                type="button"

                                onClick={
                                    () =>
                                        setMeetingSearch(
                                            ""
                                        )
                                }

                                className="text-[9px] border border-gray-300 text-gray-600 bg-white rounded-full mx-2 px-3 py-1"
                            >
                                Clear Search
                            </button>

                        )
                    }

                </div>

            </div>


            {/* Empty State */}

            {
                filteredMeetings
                    .length ===
                0
                &&
                (

                    <div
                        className="bg-white rounded m-1 p-4"
                    >

                        <p
                            className="text-sm text-gray-600"
                        >
                            No meetings match the current filters or search.
                        </p>

                    </div>

                )
            }


            {/* Meeting List */}

            {
                filteredMeetings
                    .map(
                        (
                            meeting,
                            meetingIndex
                        ) => {

                            const meetingId =
                                meeting
                                    ?._id


                            const orgName =
                                meeting
                                    ?.orgId
                                    ?.organizationName
                                ||
                                "Organization not defined"


                            const participantCount =
                                getParticipantCount(
                                    meeting
                                )


                            const participantUsers =
                                Array.isArray(
                                    meeting
                                        ?.participantUserIds
                                )
                                    ?
                                    meeting
                                        .participantUserIds
                                    :
                                    []


                            const additionalInviteEmails =
                                Array.isArray(
                                    meeting
                                        ?.additionalInviteEmails
                                )
                                    ?
                                    meeting
                                        .additionalInviteEmails
                                    :
                                    []


                            const additionalInviteCount =
                                Number.isFinite(
                                    meeting
                                        ?.additionalInviteCount
                                )
                                    ?
                                    meeting
                                        .additionalInviteCount
                                    :
                                    additionalInviteEmails
                                        .length


                            const totalInviteeCount =
                                Number.isFinite(
                                    meeting
                                        ?.totalInviteeCount
                                )
                                    ?
                                    meeting
                                        .totalInviteeCount
                                    :
                                    participantCount
                                    +
                                    additionalInviteCount


                            const boardStatus =
                                getMeetingBoardStatus(
                                    meeting
                                )


                            const recurrenceLabel =
                                getRecurrenceLabel(
                                    meeting
                                )


                            const selectedCohorts =
                                meeting
                                    ?.selectedCohortIds
                                ||
                                []

                            /*
                            |--------------------------------------------------------------------------
                            | Search Matches For This Meeting
                            |--------------------------------------------------------------------------
                            */

                            const participantSearchMatches =
                                getParticipantSearchMatches(
                                    meeting,
                                    meetingSearch
                                )


                            return (

                                <div
                                    key={
                                        meetingId
                                    }

                                    className="m-1 p-1 bg-white rounded"
                                >


                                    {/* Meeting Header */}

                                    <div
                                        className="bg-gray-100 rounded m-1 p-2 flex justify-between items-center"
                                    >

                                        <div
                                            className="flex justify-start items-center min-w-0"
                                        >

                                            <div
                                                onClick={
                                                    () =>
                                                        handleToggleDetails(
                                                            meetingId
                                                        )
                                                }

                                                className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                                            >

                                                {
                                                    showDetails[
                                                        meetingId
                                                    ]
                                                        ?
                                                        (
                                                            <MinusIcon
                                                                className="h-5 w-5 text-orange-600"
                                                            />
                                                        )
                                                        :
                                                        (
                                                            <PlusIcon
                                                                className="h-5 w-5 text-orange-600"
                                                            />
                                                        )
                                                }

                                            </div>


                                            <CalendarDaysIcon
                                                className="h-5 w-5 text-orange-600 mx-2 shrink-0"
                                            />


                                            <div
                                                className="m-1 p-1 min-w-0"
                                            >

                                                <div
                                                    className="flex justify-start items-center flex-wrap"
                                                >

                                                    <p
                                                        className="font-bold text-sm"
                                                    >
                                                        {
                                                            meetingIndex +
                                                            1
                                                        }

                                                        {" - "}

                                                        {
                                                            highlightSearchText(
                                                                meeting
                                                                    ?.meetingName
                                                                ||
                                                                "Meeting not defined",

                                                                meetingSearch
                                                            )
                                                        }

                                                        {/* {
                                                            meeting
                                                                ?.meetingName
                                                            ||
                                                            "Meeting not defined"
                                                        } */}
                                                    </p>


                                                    <p
                                                        className={`text-[9px] rounded-full mx-2 px-2 py-1 ${boardStatus.className}`}
                                                    >
                                                        {
                                                            boardStatus
                                                                .label
                                                        }
                                                    </p>


                                                    <p
                                                        className="text-[9px] bg-gray-200 text-gray-700 rounded-full mx-1 px-2 py-1"
                                                    >
                                                        Trinity:{" "}
                                                        {
                                                            participantCount
                                                        }
                                                    </p>


                                                    {
                                                        additionalInviteCount >
                                                        0
                                                        &&
                                                        (

                                                            <p
                                                                className="text-[9px] bg-orange-50 text-orange-700 rounded-full mx-1 px-2 py-1"
                                                            >
                                                                Additional:{" "}
                                                                {
                                                                    additionalInviteCount
                                                                }
                                                            </p>

                                                        )
                                                    }


                                                    <p
                                                        className="text-[9px] bg-white border border-gray-300 text-gray-700 rounded-full mx-1 px-2 py-1"
                                                    >
                                                        Total Invitees:{" "}
                                                        {
                                                            totalInviteeCount
                                                        }
                                                    </p>

                                                </div>


                                                <p
                                                    className="text-[10px] text-gray-500"
                                                >
                                                    {
                                                        orgName
                                                    }
                                                </p>


                                                {
                                                    meetingSearch
                                                        .trim()
                                                    &&
                                                    participantSearchMatches
                                                        .length >
                                                    0
                                                    &&
                                                    (

                                                        <div
                                                            className="flex justify-start items-center flex-wrap mt-1"
                                                        >

                                                            <p
                                                                className="text-[8px] text-gray-400 mr-1"
                                                            >
                                                                Match:
                                                            </p>


                                                            {
                                                                participantSearchMatches
                                                                    .map(
                                                                        (
                                                                            matchValue,
                                                                            matchIndex
                                                                        ) => (

                                                                            <span
                                                                                key={
                                                                                    `${matchValue}-${matchIndex}`
                                                                                }

                                                                                className="text-[9px] bg-white border border-yellow-300 text-gray-700 rounded-full mr-1 my-[1px] px-2 py-[1px]"
                                                                            >
                                                                                {
                                                                                    highlightSearchText(
                                                                                        matchValue,
                                                                                        meetingSearch
                                                                                    )
                                                                                }
                                                                            </span>

                                                                        )
                                                                    )
                                                            }

                                                        </div>

                                                    )
                                                }

                                            </div>

                                        </div>


                                        <div
                                            className="flex justify-start items-center mx-2"
                                        >

                                            <p
                                                className="text-[9px] bg-gray-200 text-gray-500 rounded-full px-3 py-1"
                                            >
                                                Manage in Google Calendar
                                            </p>

                                        </div>

                                    </div>


                                    {/* Summary Cards */}

                                    <div
                                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1 m-1 p-1"
                                    >

                                        <div
                                            className="bg-gray-50 rounded m-1 p-2"
                                        >

                                            <div
                                                className="flex justify-start items-center"
                                            >

                                                <CalendarDaysIcon
                                                    className="h-4 w-4 text-orange-600 mr-2"
                                                />

                                                <p
                                                    className="text-[9px] text-gray-500"
                                                >
                                                    Date
                                                </p>

                                            </div>


                                            <p
                                                className="text-xs font-bold mt-1"
                                            >
                                                {
                                                    formatMeetingDate(
                                                        meeting
                                                            ?.meetingStart,

                                                        meeting
                                                            ?.meetingTimezone
                                                    )
                                                }
                                            </p>

                                        </div>


                                        <div
                                            className="bg-gray-50 rounded m-1 p-2"
                                        >

                                            <div
                                                className="flex justify-start items-center"
                                            >

                                                <ClockIcon
                                                    className="h-4 w-4 text-orange-600 mr-2"
                                                />

                                                <p
                                                    className="text-[9px] text-gray-500"
                                                >
                                                    Time
                                                </p>

                                            </div>


                                            <p
                                                className="text-xs font-bold mt-1"
                                            >
                                                {
                                                    formatMeetingTime(
                                                        meeting
                                                            ?.meetingStart,

                                                        meeting
                                                            ?.meetingTimezone
                                                    )
                                                }
                                            </p>

                                        </div>


                                        <div
                                            className="bg-gray-50 rounded m-1 p-2"
                                        >

                                            <div
                                                className="flex justify-start items-center"
                                            >

                                                <ArrowPathIcon
                                                    className="h-4 w-4 text-orange-600 mr-2"
                                                />

                                                <p
                                                    className="text-[9px] text-gray-500"
                                                >
                                                    Repeat
                                                </p>

                                            </div>


                                            <p
                                                className="text-xs font-bold mt-1"
                                            >
                                                {
                                                    recurrenceLabel
                                                }
                                            </p>

                                        </div>


                                        <div
                                            className="bg-gray-50 rounded m-1 p-2"
                                        >

                                            <div
                                                className="flex justify-start items-center"
                                            >

                                                <UserGroupIcon
                                                    className="h-4 w-4 text-orange-600 mr-2"
                                                />

                                                <p
                                                    className="text-[9px] text-gray-500"
                                                >
                                                    Invitees
                                                </p>

                                            </div>


                                            <p
                                                className="text-xs font-bold mt-1"
                                            >
                                                {
                                                    totalInviteeCount
                                                }
                                            </p>

                                        </div>

                                    </div>


                                    {/* Expanded Details */}

                                    {
                                        showDetails[
                                            meetingId
                                        ]
                                        &&
                                        (

                                            <div
                                                className="m-1 p-1 bg-gray-200 rounded"
                                            >

                                                <div
                                                    className="m-1 p-2 rounded bg-white"
                                                >


                                                    {/* Description */}

                                                    {
                                                        meeting
                                                            ?.meetingDescription
                                                        &&
                                                        (

                                                            <div
                                                                className="flex justify-start items-start my-2"
                                                            >

                                                                <DocumentTextIcon
                                                                    className="h-4 w-4 text-orange-600 mx-2 mt-0.5 shrink-0"
                                                                />


                                                                <div>

                                                                    <p
                                                                        className="text-xs font-bold"
                                                                    >
                                                                        Description
                                                                    </p>


                                                                    <p
                                                                        className="text-xs text-gray-500 mt-1"
                                                                    >
                                                                        {
                                                                            meeting
                                                                                .meetingDescription
                                                                        }
                                                                    </p>

                                                                </div>

                                                            </div>

                                                        )
                                                    }


                                                    {/* Organization */}

                                                    <div
                                                        className="flex justify-start items-center my-2"
                                                    >

                                                        <BuildingOfficeIcon
                                                            className="h-4 w-4 text-orange-600 mx-2"
                                                        />


                                                        <p
                                                            className="text-xs font-bold"
                                                        >
                                                            Organization:{" "}

                                                            <span
                                                                className="font-normal text-gray-500"
                                                            >
                                                                {
                                                                    orgName
                                                                }
                                                            </span>

                                                        </p>

                                                    </div>


                                                    {/* Schedule */}

                                                    <div
                                                        className="flex justify-start items-center my-2"
                                                    >

                                                        <ClockIcon
                                                            className="h-4 w-4 text-orange-600 mx-2"
                                                        />


                                                        <p
                                                            className="text-xs font-bold"
                                                        >
                                                            Schedule:{" "}

                                                            <span
                                                                className="font-normal text-gray-500"
                                                            >
                                                                {
                                                                    formatMeetingDate(
                                                                        meeting
                                                                            ?.meetingStart,

                                                                        meeting
                                                                            ?.meetingTimezone
                                                                    )
                                                                }

                                                                {" · "}

                                                                {
                                                                    formatMeetingTime(
                                                                        meeting
                                                                            ?.meetingStart,

                                                                        meeting
                                                                            ?.meetingTimezone
                                                                    )
                                                                }

                                                                {" - "}

                                                                {
                                                                    formatMeetingTime(
                                                                        meeting
                                                                            ?.meetingEnd,

                                                                        meeting
                                                                            ?.meetingTimezone
                                                                    )
                                                                }
                                                            </span>

                                                        </p>

                                                    </div>


                                                    {/* Recurrence */}

                                                    <div
                                                        className="flex justify-start items-center my-2"
                                                    >

                                                        <ArrowPathIcon
                                                            className="h-4 w-4 text-orange-600 mx-2"
                                                        />


                                                        <p
                                                            className="text-xs font-bold"
                                                        >
                                                            Recurrence:{" "}

                                                            <span
                                                                className="font-normal text-gray-500"
                                                            >
                                                                {
                                                                    recurrenceLabel
                                                                }
                                                            </span>

                                                        </p>

                                                    </div>


                                                    {/* Selected Cohorts */}

                                                    {
                                                        selectedCohorts
                                                            .length >
                                                        0
                                                        &&
                                                        (

                                                            <div
                                                                className="my-2"
                                                            >

                                                                <div
                                                                    className="flex justify-start items-center"
                                                                >

                                                                    <UserGroupIcon
                                                                        className="h-4 w-4 text-orange-600 mx-2"
                                                                    />


                                                                    <p
                                                                        className="text-xs font-bold"
                                                                    >
                                                                        Selected Cohorts:{" "}

                                                                        <span
                                                                            className="font-normal text-gray-500"
                                                                        >
                                                                            {
                                                                                selectedCohorts
                                                                                    .length
                                                                            }
                                                                        </span>

                                                                    </p>

                                                                </div>


                                                                <div
                                                                    className="flex flex-wrap mx-7 my-1"
                                                                >

                                                                    {
                                                                        selectedCohorts
                                                                            .map(
                                                                                (
                                                                                    cohort,
                                                                                    cohortIndex
                                                                                ) => (

                                                                                    <p
                                                                                        key={
                                                                                            getRefId(
                                                                                                cohort
                                                                                            )
                                                                                            ||
                                                                                            cohortIndex
                                                                                        }

                                                                                        className="text-[9px] bg-orange-50 text-orange-700 rounded-full mx-1 my-1 px-2 py-1"
                                                                                    >
                                                                                        {
                                                                                            cohort
                                                                                                ?.cohortName
                                                                                            ||
                                                                                            cohort
                                                                                                ?.cohortCode
                                                                                            ||
                                                                                            "Cohort"
                                                                                        }
                                                                                    </p>

                                                                                )
                                                                            )
                                                                    }

                                                                </div>

                                                            </div>

                                                        )
                                                    }


                                                    {/* Trinity Participant Count */}

                                                    <div
                                                        className="flex justify-start items-center my-2"
                                                    >

                                                        <UserGroupIcon
                                                            className="h-4 w-4 text-orange-600 mx-2"
                                                        />


                                                        <p
                                                            className="text-xs font-bold"
                                                        >
                                                            Trinity Participants:{" "}

                                                            <span
                                                                className="font-normal text-gray-500"
                                                            >
                                                                {
                                                                    participantCount
                                                                }
                                                            </span>

                                                        </p>

                                                    </div>


                                                    {/* Trinity Participant Detail */}

                                                    {
                                                        participantUsers
                                                            .length >
                                                        0
                                                        &&
                                                        (

                                                            <div
                                                                className="mx-7 my-3"
                                                            >

                                                                <p
                                                                    className="text-[10px] font-bold text-gray-700 mb-2"
                                                                >
                                                                    Trinity Invitees
                                                                </p>


                                                                <div
                                                                    className="grid grid-cols-1 md:grid-cols-2 gap-2"
                                                                >

                                                                    {
                                                                        participantUsers
                                                                            .map(
                                                                                (
                                                                                    participant,
                                                                                    participantIndex
                                                                                ) => {

                                                                                    const participantName =
                                                                                        getParticipantName(
                                                                                            participant
                                                                                        )


                                                                                    const participantEmail =
                                                                                        getParticipantEmail(
                                                                                            participant
                                                                                        )


                                                                                    return (

                                                                                        <div
                                                                                            key={
                                                                                                getRefId(
                                                                                                    participant
                                                                                                )
                                                                                                ||
                                                                                                participantIndex
                                                                                            }

                                                                                            className="bg-gray-100 border border-gray-200 rounded p-2"
                                                                                        >

                                                                                            <div
                                                                                                className="flex justify-start items-start"
                                                                                            >

                                                                                                <UserGroupIcon
                                                                                                    className="h-4 w-4 text-orange-600 mr-2 mt-0.5 shrink-0"
                                                                                                />


                                                                                                <div>

                                                                                                    <p
                                                                                                        className="text-[10px] font-bold text-gray-700"
                                                                                                    >
                                                                                                        {
                                                                                                            highlightSearchText(
                                                                                                                participantName
                                                                                                                ||
                                                                                                                "Participant name unavailable",

                                                                                                                meetingSearch
                                                                                                            )
                                                                                                        }

                                                                                                        {/* {
                                                                                                            participantName
                                                                                                            ||
                                                                                                            "Participant name unavailable"
                                                                                                        } */}
                                                                                                    </p>


                                                                                                    {
                                                                                                        participantEmail
                                                                                                        &&
                                                                                                        (

                                                                                                            <div
                                                                                                                className="flex justify-start items-center mt-1"
                                                                                                            >

                                                                                                                <EnvelopeIcon
                                                                                                                    className="h-3 w-3 text-gray-400 mr-1"
                                                                                                                />


                                                                                                                <p
                                                                                                                    className="text-[9px] text-gray-500"
                                                                                                                >
                                                                                                                    {
                                                                                                                        highlightSearchText(
                                                                                                                            participantEmail,
                                                                                                                            meetingSearch
                                                                                                                        )
                                                                                                                    }
                                                                                                                    {/* {
                                                                                                                        participantEmail
                                                                                                                    } */}
                                                                                                                </p>

                                                                                                            </div>

                                                                                                        )
                                                                                                    }

                                                                                                </div>

                                                                                            </div>

                                                                                        </div>

                                                                                    )

                                                                                }
                                                                            )
                                                                    }

                                                                </div>

                                                            </div>

                                                        )
                                                    }


                                                    {/* Additional Invitees */}

                                                    {
                                                        additionalInviteEmails
                                                            .length >
                                                        0
                                                        &&
                                                        (

                                                            <div
                                                                className="mx-7 my-3"
                                                            >

                                                                <p
                                                                    className="text-[10px] font-bold text-gray-700 mb-2"
                                                                >
                                                                    Additional Email Invitees
                                                                </p>


                                                                <div
                                                                    className="flex flex-wrap"
                                                                >

                                                                    {
                                                                        additionalInviteEmails
                                                                            .map(
                                                                                (
                                                                                    inviteEmail,
                                                                                    inviteIndex
                                                                                ) => (

                                                                                    <div
                                                                                        key={
                                                                                            `${inviteEmail}-${inviteIndex}`
                                                                                        }

                                                                                        className="flex justify-start items-center bg-orange-50 border border-orange-200 text-orange-700 rounded-full mx-1 my-1 px-3 py-1"
                                                                                    >

                                                                                        <EnvelopeIcon
                                                                                            className="h-3 w-3 mr-1"
                                                                                        />


                                                                                        <p
                                                                                            className="text-[9px]"
                                                                                        >
                                                                                            {
                                                                                                highlightSearchText(
                                                                                                    inviteEmail,
                                                                                                    meetingSearch
                                                                                                )
                                                                                            }
                                                                                            
                                                                                            {/* {
                                                                                                inviteEmail
                                                                                            } */}
                                                                                        </p>

                                                                                    </div>

                                                                                )
                                                                            )
                                                                    }

                                                                </div>

                                                            </div>

                                                        )
                                                    }


                                                    {/* Calendar Status */}

                                                    <div
                                                        className="flex justify-start items-center my-2"
                                                    >

                                                        {
                                                            boardStatus
                                                                .key ===
                                                            "scheduled"
                                                                ?
                                                                (
                                                                    <CheckCircleIcon
                                                                        className="h-4 w-4 text-green-600 mx-2"
                                                                    />
                                                                )
                                                                :
                                                                boardStatus
                                                                    .key ===
                                                                "needs_attention"
                                                                    ?
                                                                    (
                                                                        <ExclamationTriangleIcon
                                                                            className="h-4 w-4 text-red-600 mx-2"
                                                                        />
                                                                    )
                                                                    :
                                                                    (
                                                                        <CalendarDaysIcon
                                                                            className="h-4 w-4 text-orange-600 mx-2"
                                                                        />
                                                                    )
                                                        }


                                                        <p
                                                            className="text-xs font-bold"
                                                        >
                                                            Google Calendar:{" "}

                                                            <span
                                                                className="font-normal text-gray-500"
                                                            >
                                                                {
                                                                    boardStatus
                                                                        .label
                                                                }
                                                            </span>

                                                        </p>

                                                    </div>


                                                    {/* Calendar Error */}

                                                    {
                                                        meeting
                                                            ?.calendarCreationStatus ===
                                                        "failed"
                                                        &&
                                                        meeting
                                                            ?.calendarCreationError
                                                        &&
                                                        (

                                                            <div
                                                                className="bg-red-50 rounded text-xs mx-2 px-2 my-2 py-2"
                                                            >

                                                                <p
                                                                    className="font-bold m-1 text-red-700"
                                                                >
                                                                    Calendar Creation Error
                                                                </p>


                                                                <p
                                                                    className="m-1 text-red-600"
                                                                >
                                                                    {
                                                                        meeting
                                                                            .calendarCreationError
                                                                    }
                                                                </p>

                                                            </div>

                                                        )
                                                    }


                                                    {/* Google Links */}

                                                    {
                                                        (
                                                            meeting
                                                                ?.googleCalendarHtmlLink
                                                            ||
                                                            meeting
                                                                ?.googleMeetLink
                                                        )
                                                        &&
                                                        (

                                                            <div
                                                                className="flex justify-start items-center flex-wrap mx-2 mt-3"
                                                            >

                                                                {
                                                                    meeting
                                                                        ?.googleCalendarHtmlLink
                                                                    &&
                                                                    (

                                                                        <a
                                                                            href={
                                                                                meeting
                                                                                    .googleCalendarHtmlLink
                                                                            }

                                                                            target="_blank"

                                                                            rel="noreferrer"

                                                                            className="text-[10px] bg-orange-100 text-orange-700 rounded-full mr-2 px-3 py-1 cursor-pointer"
                                                                        >
                                                                            Open Google Calendar
                                                                        </a>

                                                                    )
                                                                }


                                                                {
                                                                    meeting
                                                                        ?.googleMeetLink
                                                                    &&
                                                                    (

                                                                        <a
                                                                            href={
                                                                                meeting
                                                                                    .googleMeetLink
                                                                            }

                                                                            target="_blank"

                                                                            rel="noreferrer"

                                                                            className="text-[10px] bg-blue-50 text-blue-700 rounded-full mr-2 px-3 py-1 cursor-pointer flex justify-start items-center"
                                                                        >

                                                                            <VideoCameraIcon
                                                                                className="h-3 w-3 mr-1"
                                                                            />

                                                                            Google Meet

                                                                        </a>

                                                                    )
                                                                }

                                                            </div>

                                                        )
                                                    }

                                                </div>

                                            </div>

                                        )
                                    }

                                </div>

                            )

                        }
                    )
            }

        </div>

    )

}


export default EtTrinityMeetingList